import * as focusTrap from 'focus-trap';
import debounce from 'debounce-promise';
import fetch from 'app/utilities/fetch';
import { formatSearchResults } from 'app/utilities/format-data';
import FullScreenDialog from './full-screen-dialog';
import Grid from './grid';
import Icon from './icon';
import { isAppProd } from 'config/env';
import Loader from './loader';
import Paginator from './paginator';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { ENDPOINTS, RESPONSE } from 'config/api';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const SearchDialog = ({ hideSearchDialog, isActive }) => {
    const focusTrapRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const FullScreenDialogRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(searchParams.get('modalPage') || 1);
    const [searchText, setSearchText] = useState(searchParams.get('keyword') || '');
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResponse, setSearchResponse] = useState({
        results: [],
        links: {},
        count: null,
        // recording the item index to focus on after new results have loaded and rendered
        nextFocus: null
    });

    const resetSearchResults = () => {
        setResults(null, {}, []);
        setError(false);
    };

    const setResults = (count, links, results, page = 1, nextFocus = null) => {
        setSearchResponse({
            count,
            links,
            results,
            nextFocus,
        });
        setCurrentPage(page);
    };

    const searchTextChangeHandler = (event) => {
        setSearchText(event.target.value);
        resetSearchResults();
        debouncedSearchResultsAction(event.target.value, 1);
    };

    const buildSearchEndpoint = (keyword, page) => {
        let endpoint = `${ENDPOINTS.SEARCH}?keywords=${encodeURI(keyword)}&page=${page + 1}&is_published=true`;
        // for debugging search query in non prod envs
        // allows us to artificially set the date we are searching on instead of today
        // date format = YYYY-MM-DD
        if (!isAppProd) {
            // use a test query param that doesn't conflict with normal params
            const searchDate = searchParams.get('_debugdate');
            if (searchDate) {
                endpoint += `&_debugdate=${searchDate}`;
            }
        }

        return endpoint;
    };

    const searchResults = async(keyword, page = 1) => {
        if (!keyword) {
            return;
        }
        setIsLoading(true);
        try {
            const fetchArray = [...Array(parseInt(page)).keys()];
            const response = await Promise.all(fetchArray.map((item) => fetch(buildSearchEndpoint(keyword, item))));
            const { count, links, results } = formatSearchResults(response);
            setResults(count, links, results, page);
        } catch (err) {
            if (err.status === RESPONSE.NOT_FOUND) {
                setResults(0, {}, []);
            } else {
                setError(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearchResultsAction = useCallback(
        // eslint-disable-next-line no-magic-numbers
        debounce((keyword, page) => searchResults(keyword, page), 1000), []
    );

    useEffect(() => {
        // init focus trap for search dialog
        focusTrapRef.current = focusTrap.createFocusTrap(document.getElementById('global-search-dialog'));

        if (searchText) {
            searchResults(searchText, currentPage);
        }
    }, []);

    useEffect(() => {
        if (isActive) {
            focusTrapRef.current.activate();
        } else {
            focusTrapRef.current.deactivate();
        }
    }, [isActive]);

    useEffect(() => {
        if (searchText) {
            searchParams.set('keyword', searchText);

            if (currentPage && currentPage > 1) {
                searchParams.set('modalPage', currentPage);
            } else {
                searchParams.delete('modalPage');
            }
        } else {
            searchParams.delete('keyword');
            searchParams.delete('modalPage');
        }
        setSearchParams(searchParams);
    }, [searchText, currentPage]);

    const paginatorClickHandler = async() => {
        try {
            const response = await fetch(buildSearchEndpoint(searchText, parseInt(currentPage)));
            const { links, results } = formatSearchResults([response]);
            const newResults = [...searchResponse.results, ...results];
            // record the index of the first item in the new results
            // so we can focus to it after render has finished
            const nextFocus = newResults.length > searchResponse.results.length ? searchResponse.results.length : newResults.length - 1;
            setResults(
                searchResponse.count,
                links,
                newResults,
                parseInt(currentPage) + 1,
                nextFocus,
            );
        } catch (err) {
            setError(true);
        }
    };

    const renderSearchResultsMsg = () => {
        if (error) {
            return (<p className="message">Oops, looks like something went wrong. <br/>Please try your search again.</p>);
        }

        if (!searchResponse.count || searchResponse.count === 0) {
            return (
                <p className="message">Your search has no results – but we still have lots to show you!<br/>Try using a different search keyword.</p>
            );
        }

        return (
            <p className="message">We found {searchResponse.count} results.</p>
        );
    };

    const renderResults = () => {
        return (
            <React.Fragment>
                <Paginator currentItemsLength={searchResponse.results.length} total={searchResponse.count} btnClickHandler={paginatorClickHandler}>
                    <Grid
                        data={searchResponse.results}
                        isAutoScroll={true}
                        isDialog={true}
                        hasLine={false}
                        scrollRef={FullScreenDialogRef}
                        // tell grid which item to focus on after load more has been clicked
                        nextFocus={searchResponse.nextFocus}
                    />
                </Paginator>
            </React.Fragment>
        );
    };

    return (
        <FullScreenDialog
            extraClass="search-dialog"
            id="global-search-dialog"
            ref={FullScreenDialogRef}
            isActive={isActive}
            style={{ display: isActive ? 'flex' : 'none' }}
        >
            <form className="form search-dialog-form" onSubmit={(event) => event.preventDefault()}>
                <h2 className="sr-only">Search</h2>
                <label className="h5 heading" htmlFor="search-dialog-input">Type in your search and press enter</label>
                <input
                    id="search-dialog-input"
                    className="input"
                    name="query"
                    type="text"
                    autoComplete="off"
                    value={searchText}
                    onChange={searchTextChangeHandler}
                    autoFocus
                />
            </form>
            <div
                className="search-results-wrapper"
            >
                {/* Search results message must always be mounted for changes to be announced */}
                <div aria-live="polite">
                    {isLoading && <Loader />}
                    {searchResponse.count !== null && renderSearchResultsMsg()}
                </div>
                {searchText && (
                    <>
                        {/* invisible heading for accessibility only */}
                        <h2 className="sr-only">Search results</h2>
                        {searchResponse.count !== null && renderResults()}
                    </>
                )}
            </div>
            <button className="button no-border close-button" onClick={hideSearchDialog}
                aria-label="Close search dialog"
            >
                <Icon className="icon large" name="close" />
            </button>
        </FullScreenDialog>
    );
};

SearchDialog.propTypes = {
    hideSearchDialog: PropTypes.func.isRequired,
    isActive: PropTypes.bool.isRequired,
};

export default SearchDialog;
